import { FC } from 'react'

import { useAtomValue } from 'jotai'
import {
  Text,
  GreyGrey80,
  Popover,
  Primary13SemiBold,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  isEditingDisabledAtom,
  showValidationResultsAtom,
  validationPopoverKeyAtom,
  validationResultsAtom
} from 'Automations/automationEditor/atoms'
import { countValidationResultsByLevel } from 'Automations/nodeDefinitions'
import { ValidationResult } from './ValidationResult'
import { Trigger } from './Trigger'
import { flattenValidationResults } from './flattenValidationResults'
import { CountPill } from './CountPill'

export const WorkflowErrors: FC = () => {
  const validationResults = useAtomValue(validationResultsAtom)
  const showValidationResults = useAtomValue(showValidationResultsAtom)
  const validationPopoverKey = useAtomValue(validationPopoverKeyAtom)
  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)

  if (!showValidationResults || isEditingDisabled) return null

  const errorCount = countValidationResultsByLevel({
    validationResults,
    level: 'ERROR'
  })

  const warningCount = countValidationResultsByLevel({
    validationResults,
    level: 'WARN'
  })

  if (!errorCount && !warningCount) return null

  return (
    <Box position="absolute" bottom="12px" left="12px">
      <Popover
        key={validationPopoverKey}
        variant="standard-v2"
        content={
          <Content errorCount={errorCount} warningCount={warningCount} />
        }
        trigger="click"
        position="top-start"
        gutter={12}
        showArrow={false}
        maxWidth="400px"
        defaultIsOpen
      >
        <Trigger errorCount={errorCount} warningCount={warningCount} />
      </Popover>
    </Box>
  )
}

const Content: FC<{ errorCount: number; warningCount: number }> = ({
  errorCount,
  warningCount
}) => {
  const validationResults = useAtomValue(validationResultsAtom)
  const flatValidationResults = flattenValidationResults(validationResults)

  return (
    <Flex
      flexDir="column"
      width="384px"
      gap="8px"
      data-testid="automations.editor.workflowErrors.popover"
    >
      <Flex padding="4px" alignItems="center" gap="8px">
        {errorCount > 0 && (
          <>
            <Text {...Primary13SemiBold} color={GreyGrey80}>
              Errors
            </Text>
            <CountPill count={errorCount} level="ERROR" />
          </>
        )}

        {warningCount > 0 && (
          <>
            <Text {...Primary13SemiBold} color={GreyGrey80}>
              Warnings
            </Text>
            <CountPill count={warningCount} level="WARN" />
          </>
        )}
      </Flex>

      <Flex flexDir="column">
        {flatValidationResults.map(({ key, ...props }) => (
          <ValidationResult key={key} {...props} />
        ))}
      </Flex>
    </Flex>
  )
}
